import { useState, useEffect } from "react";
import { GetBillingData } from "../../../services/billingService";
import { subscribeBillingRefresh } from "../../../utils/billingRefreshEvent";
import { subscribeTenantChange } from "../../../utils/tenantChangeEvent";
import { resolveImageUrl } from "../../../utils";

interface BillingItem {
  BillingTypeId: number;
  BillingTypeName: string;
  Billing: number;
  Paid: number;
  Outstanding: number;
}

interface BillingResponse {
  billingDatas: BillingItem[];
  TotalBilling: number;
  TotalPaid: number;
  TotalOutstanding: number;
  DueDate: string;
  LastUpdate: string;
  Code: number;
  Message: string;
}

const BILLING_FALLBACK: BillingResponse = {
  billingDatas: [],
  TotalBilling: 0,
  TotalPaid: 0,
  TotalOutstanding: 0,
  DueDate: "",
  LastUpdate: "",
  Code: -1,
  Message: "",
};

const DEFAULT_ITEMS: BillingItem[] = [
  { BillingTypeId: 1, BillingTypeName: "Rental", Billing: 0, Paid: 0, Outstanding: 0 },
  { BillingTypeId: 2, BillingTypeName: "Service Charge", Billing: 0, Paid: 0, Outstanding: 0 },
  { BillingTypeId: 3, BillingTypeName: "Electricity", Billing: 0, Paid: 0, Outstanding: 0 },
  { BillingTypeId: 4, BillingTypeName: "Water", Billing: 0, Paid: 0, Outstanding: 0 },
  { BillingTypeId: 5, BillingTypeName: "Gas", Billing: 0, Paid: 0, Outstanding: 0 },
];

export default function BillingSection() {
  const [billingData, setBillingData] = useState<BillingResponse | null>(null);

  useEffect(() => {
    console.log("[BILLING] component mounted");
  }, []);

  useEffect(() => {
    const fetchBillingData = async () => {
      console.log("[BILLING] initial fetch, tenant_id:", localStorage.getItem("tenant_id"));
      try {
        const data = await GetBillingData(false);
        console.log("[BILLING] initial fetch response:", data);
        if (data) {
          setBillingData(data ?? BILLING_FALLBACK);
        }
      } catch (err) {
        console.log("[BILLING] initial fetch error:", err);
        setBillingData(BILLING_FALLBACK);
      }
    };

    fetchBillingData();
  }, []);

  useEffect(() => {
    const handleRefresh = async () => {
      console.log("[BILLING] handleRefresh fired, tenant_id:", localStorage.getItem("tenant_id"));
      try {
        // isreload=true hanya saat user klik tombol refresh manual
        const data = await GetBillingData(true);
        console.log("[BILLING] refresh response:", data);
        if (data) {
          setBillingData(data ?? BILLING_FALLBACK);
        }
      } catch (err) {
        console.log("[BILLING] refresh error:", err);
      }
    };

    const unsubscribe = subscribeBillingRefresh(handleRefresh);
    console.log("[BILLING] subscribed to billingRefresh");
    return () => {
      console.log("[BILLING] unsubscribed from billingRefresh");
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleTenantChange = async () => {
      console.log("[BILLING] handleTenantChange fired!");
      console.log("[BILLING] tenant_id from storage:", localStorage.getItem("tenant_id"));
      try {
        // ✅ isreload=false (isreload=0) saat ganti tenant
        const data = await GetBillingData(false);
        console.log("[BILLING] tenant change fetch response:", data);
        if (data) {
          setBillingData(data ?? BILLING_FALLBACK);
        }
      } catch (err) {
        console.log("[BILLING] tenant change fetch error:", err);
        setBillingData(BILLING_FALLBACK);
      }
    };

    const unsubscribe = subscribeTenantChange(handleTenantChange);
    console.log("[BILLING] subscribed to tenantChange");
    return () => {
      console.log("[BILLING] unsubscribed from tenantChange");
      unsubscribe();
    };
  }, []);

  const formatCurrency = (value: number) => {
    return `Rp. ${new Intl.NumberFormat("id-ID").format(Math.trunc(value))}`;
  };

  const getBillingMonth = () => {
    const now = new Date();
    const day = now.getDate();
    let targetDate = now;
    if (day <= 5) {
      targetDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    }
    return targetDate.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  };

  const getImageForBillingType = (typeId: number) => {
    const imageMap: { [key: number]: string } = {
      1: resolveImageUrl("/images/biling (1).png"),
      2: resolveImageUrl("/images/biling (2).png"),
      3: resolveImageUrl("/images/biling (3).png"),
      4: resolveImageUrl("/images/biling (4).png"),
      5: resolveImageUrl("/images/biling (5).png"),
    };
    return imageMap[typeId] || resolveImageUrl("/images/biling (1).png");
  };

  const billingItems = (billingData?.billingDatas ?? []).filter(
    (item) => !item.BillingTypeName.toLowerCase().includes("other")
  );

  const displayItems = billingItems.length > 0 ? billingItems : DEFAULT_ITEMS;

  return (
    <div className="h-full flex flex-col w-full flex-1">
      <div className="rounded-2xl bg-white p-4 shadow-sm border border-gray-200 dark:border-gray-700 dark:bg-gray-900 overflow-hidden flex flex-col h-full flex-1">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-[13px] font-bold text-gray-900 dark:text-white">
            Date Billing: {getBillingMonth()}
          </h3>
        </div>

        <div className="space-y-1.5 flex-1 overflow-y-auto">
          {displayItems.map((item, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between overflow-hidden py-1"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                  <img
                    src={getImageForBillingType(item.BillingTypeId)}
                    alt={item.BillingTypeName}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-[11.5px] font-medium text-gray-900 dark:text-white truncate">
                  {item.BillingTypeName}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[11.5px] font-semibold text-gray-900 dark:text-white whitespace-nowrap text-right">
                  {formatCurrency(item.Billing)}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-auto pt-2">
        </div>
      </div>
    </div>
  );
}